// Scope-selection state + grid-membership rules.
//
// Two distinct caps (LLD 13 §5):
//   - CAM_CAP=12  — how many cameras can sit on the timeline (event metadata
//                   is cheap; backend's true ceiling is events:search ≤ 50,
//                   we stay calmer).
//   - PLAY_CAP=4  — how many videos play simultaneously in the synced grid
//                   (4× SD ≈ 12 MB/min total; 4× HD is not viable).
//
// Grid membership uses a chart-legend "restore last" model (LLD 13 §9):
//   - `gridSel`  — recency-ordered set currently in the grid (≤ PLAY_CAP).
//   - `gridBench`— LIFO of evicted cameras; removing a grid camera pulls
//                  the most-recently-benched camera back in.
//
// `gridInit` gates auto-fill on timeline-add: before the user has opened
// the grid view at least once, newly-added timeline cameras don't claim
// a grid slot — the seed happens on first open.

import { useCallback, useState } from "react";

export const CAM_CAP = 12;
export const PLAY_CAP = 4;

/** Camera fields the timeline + scope rail need to render. */
export interface ScopeCamera {
  doorbot_id: string;
  location_id: string;
  name: string;
  has_real: boolean;
  online: boolean;
}

/** Returned by `useScopeSelection`. */
export interface ScopeSelectionApi {
  /** doorbot_id -> camera. Map preserves insertion order = timeline lane order. */
  selected: ReadonlyMap<string, ScopeCamera>;
  /** Cameras currently in the grid, in grid-display order. */
  gridSel: readonly string[];
  /** LIFO of cameras evicted from the grid (most-recently benched at the end). */
  gridBench: readonly string[];
  /** True after the grid view has been opened at least once. */
  gridInit: boolean;
  /** Hit when a user tries to add past CAM_CAP. */
  capReached: boolean;

  /** True if the camera is on the timeline. */
  isSelected(id: string): boolean;
  /** True if the camera is currently in the grid. */
  inGrid(id: string): boolean;
  /** Select / deselect a camera (idempotent). Returns false on cap-reached. */
  toggleCamera(cam: ScopeCamera, on: boolean): boolean;
  /** Bulk add/remove all cameras at a location. Stops at CAM_CAP on add. */
  toggleLocation(cams: readonly ScopeCamera[], add: boolean): void;
  /** Toggle a camera's grid membership (chart-legend restore-last rules). */
  toggleGrid(id: string): void;
  /** Mark the grid as initialised — call when the user opens the grid view. */
  initGrid(): void;
  /** Imperatively dismiss the cap-reached banner. */
  clearCapBanner(): void;
}

/**
 * Initial grid seed: the first PLAY_CAP cameras on the timeline land in the
 * grid; the rest go to the bench (LIFO order, oldest at the bottom).
 */
function seedGridFromSelection(selected: ReadonlyMap<string, ScopeCamera>): {
  gridSel: string[];
  gridBench: string[];
} {
  const ids = [...selected.keys()];
  return {
    gridSel: ids.slice(0, PLAY_CAP),
    gridBench: ids.slice(PLAY_CAP),
  };
}

export function useScopeSelection(): ScopeSelectionApi {
  const [selected, setSelected] = useState<ReadonlyMap<string, ScopeCamera>>(
    () => new Map(),
  );
  const [gridSel, setGridSel] = useState<readonly string[]>(() => []);
  const [gridBench, setGridBench] = useState<readonly string[]>(() => []);
  const [gridInit, setGridInit] = useState(false);
  const [capReached, setCapReached] = useState(false);

  const isSelected = useCallback(
    (id: string) => selected.has(id),
    [selected],
  );
  const inGrid = useCallback(
    (id: string) => gridSel.includes(id),
    [gridSel],
  );

  /**
   * After the grid has been opened once, a newly-added timeline camera
   * fills an empty grid slot (else it waits on the bench). Before first
   * open, the initial seed (in `initGrid`) handles ordering.
   */
  const onTimelineAddForGrid = useCallback(
    (id: string, prevSel: readonly string[], prevBench: readonly string[]) => {
      if (!gridInit) return { gridSel: prevSel, gridBench: prevBench };
      if (prevSel.includes(id)) return { gridSel: prevSel, gridBench: prevBench };
      if (prevSel.length < PLAY_CAP) {
        return { gridSel: [...prevSel, id], gridBench: prevBench };
      }
      return { gridSel: prevSel, gridBench: [...prevBench, id] };
    },
    [gridInit],
  );

  /**
   * On removal: pull the most-recently-benched camera back into the grid
   * (chart-legend restore-last). Forgets the camera on the bench too in
   * case it sat there.
   */
  const onTimelineRemoveForGrid = useCallback(
    (
      id: string,
      isStillSelected: (other: string) => boolean,
      prevSel: readonly string[],
      prevBench: readonly string[],
    ) => {
      const nextSel = prevSel.slice();
      let nextBench = prevBench.slice();
      const i = nextSel.indexOf(id);
      if (i >= 0) {
        nextSel.splice(i, 1);
        // Restore the most-recently-benched camera that's still on the timeline.
        while (nextBench.length > 0) {
          const back = nextBench[nextBench.length - 1]!;
          nextBench = nextBench.slice(0, -1);
          if (isStillSelected(back) && !nextSel.includes(back)) {
            nextSel.push(back);
            break;
          }
        }
      }
      const b = nextBench.indexOf(id);
      if (b >= 0) {
        nextBench = nextBench.slice(0, b).concat(nextBench.slice(b + 1));
      }
      return { gridSel: nextSel, gridBench: nextBench };
    },
    [],
  );

  const toggleCamera = useCallback(
    (cam: ScopeCamera, on: boolean): boolean => {
      let allowed = true;
      setSelected((prev) => {
        if (on) {
          if (prev.has(cam.doorbot_id)) return prev;
          if (prev.size >= CAM_CAP) {
            allowed = false;
            return prev;
          }
          const next = new Map(prev);
          next.set(cam.doorbot_id, cam);
          return next;
        }
        if (!prev.has(cam.doorbot_id)) return prev;
        const next = new Map(prev);
        next.delete(cam.doorbot_id);
        return next;
      });
      if (!allowed) {
        setCapReached(true);
        return false;
      }
      // Adjust the grid sets in lockstep using the freshly-updated `selected`
      // closure. `setSelected` above is async, so we re-derive here.
      if (on) {
        setGridSel((prevSel) => {
          let nextSel = prevSel;
          setGridBench((prevBench) => {
            const r = onTimelineAddForGrid(cam.doorbot_id, prevSel, prevBench);
            nextSel = r.gridSel;
            return r.gridBench;
          });
          return nextSel;
        });
      } else {
        setGridSel((prevSel) => {
          let nextSel = prevSel;
          setGridBench((prevBench) => {
            const r = onTimelineRemoveForGrid(
              cam.doorbot_id,
              (other) =>
                other !== cam.doorbot_id &&
                // post-removal: the camera leaving is no longer selected
                // either, so we just compare against the rest of prev.
                true,
              prevSel,
              prevBench,
            );
            nextSel = r.gridSel;
            return r.gridBench;
          });
          return nextSel;
        });
      }
      return true;
    },
    [onTimelineAddForGrid, onTimelineRemoveForGrid],
  );

  const toggleLocation = useCallback(
    (cams: readonly ScopeCamera[], add: boolean): void => {
      let hitCap = false;
      setSelected((prev) => {
        const next = new Map(prev);
        if (add) {
          for (const cam of cams) {
            if (next.size >= CAM_CAP) {
              hitCap = true;
              break;
            }
            if (!next.has(cam.doorbot_id)) next.set(cam.doorbot_id, cam);
          }
        } else {
          for (const cam of cams) next.delete(cam.doorbot_id);
        }
        return next;
      });
      if (hitCap) setCapReached(true);
      // Recompute grid in lockstep. We only handle the bulk-remove case
      // (bulk-add never auto-fills the grid past PLAY_CAP — same rule as
      // single toggleCamera). For bulk add we still call onTimelineAddForGrid
      // per id so newly-added cameras can claim grid slots.
      if (add) {
        setGridSel((prevSel) => {
          let nextSel = prevSel;
          let nextBench: readonly string[] = [];
          setGridBench((prevBench) => {
            nextBench = prevBench;
            for (const cam of cams) {
              const r = onTimelineAddForGrid(cam.doorbot_id, nextSel, nextBench);
              nextSel = r.gridSel;
              nextBench = r.gridBench;
            }
            return nextBench;
          });
          return nextSel;
        });
      } else {
        setGridSel((prevSel) => {
          let nextSel = prevSel;
          setGridBench((prevBench) => {
            let nextBench = prevBench;
            for (const cam of cams) {
              const r = onTimelineRemoveForGrid(
                cam.doorbot_id,
                () => true,
                nextSel,
                nextBench,
              );
              nextSel = r.gridSel;
              nextBench = r.gridBench;
            }
            return nextBench;
          });
          return nextSel;
        });
      }
    },
    [onTimelineAddForGrid, onTimelineRemoveForGrid],
  );

  const toggleGrid = useCallback((id: string): void => {
    if (!selected.has(id)) return;
    setGridSel((prevSel) => {
      const nextSel = prevSel.slice();
      const i = nextSel.indexOf(id);
      if (i >= 0) {
        // REMOVE
        nextSel.splice(i, 1);
        setGridBench((prevBench) => {
          let nextBench = prevBench.slice();
          while (nextBench.length > 0) {
            const back = nextBench[nextBench.length - 1]!;
            nextBench = nextBench.slice(0, -1);
            if (selected.has(back) && !nextSel.includes(back)) {
              nextSel.push(back);
              break;
            }
          }
          return nextBench;
        });
      } else {
        // ADD
        if (nextSel.length >= PLAY_CAP) {
          const evicted = nextSel.shift()!;
          setGridBench((prevBench) => [...prevBench, evicted]);
        }
        nextSel.push(id);
      }
      return nextSel;
    });
  }, [selected]);

  const initGrid = useCallback(() => {
    setGridInit((prev) => {
      if (prev) return prev;
      // Seed grid from the current timeline selection (first PLAY_CAP cameras
      // land in the grid; rest go to the bench LIFO).
      const seeded = seedGridFromSelection(selected);
      setGridSel(seeded.gridSel);
      setGridBench(seeded.gridBench);
      return true;
    });
  }, [selected]);

  const clearCapBanner = useCallback(() => setCapReached(false), []);

  return {
    selected,
    gridSel,
    gridBench,
    gridInit,
    capReached,
    isSelected,
    inGrid,
    toggleCamera,
    toggleLocation,
    toggleGrid,
    initGrid,
    clearCapBanner,
  };
}
